import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile-js";
const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TURNSTILE_TIMEOUT = 60000;

let turnstileScriptPromise = null;

const turnstileError = (message, turnstileDetails = {}) => {
  const error = new Error(message);
  error.turnstileDetails = turnstileDetails;
  return error;
};

const loadTurnstileScript = () => {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    let script = document.getElementById(TURNSTILE_SCRIPT_ID);

    if (script && !window.turnstile) {
      script.remove();
      script = null;
    }

    const resolveWhenReady = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        reject(turnstileError("Turnstile failed to initialize", { phase: "script_load" }));
      }
    };

    if (!script) {
      script = document.createElement("script");
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = false;
    }

    script.addEventListener("load", resolveWhenReady, { once: true });
    script.addEventListener("error", (event) => reject(turnstileError("Turnstile failed to load", {
      phase: "script_load",
      event_type: event.type,
      script_src: TURNSTILE_SCRIPT_SRC
    })), { once: true });

    document.head.appendChild(script);
  }).catch((error) => {
    const script = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (script && !window.turnstile) {
      script.remove();
    }

    turnstileScriptPromise = null;
    throw error;
  });

  return turnstileScriptPromise;
};

const TurnstileChallenge = forwardRef(({ sitekey }, ref) => {
  const containerRef = useRef(null);
  const containerIdRef = useRef(`turnstile-checkout-${Math.random().toString(36).slice(2)}`);
  const mountedRef = useRef(false);
  const pendingResolveRef = useRef(null);
  const pendingRejectRef = useRef(null);
  const renderPromiseRef = useRef(null);
  const timeoutRef = useRef(null);
  const widgetIdRef = useRef(null);

  const clearPendingChallenge = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    pendingResolveRef.current = null;
    pendingRejectRef.current = null;
  };

  const resolveChallenge = (token) => {
    if (!pendingResolveRef.current) return;

    const resolve = pendingResolveRef.current;
    clearPendingChallenge();
    resolve(token);
  };

  const rejectChallenge = (error) => {
    if (!pendingRejectRef.current) return;

    const reject = pendingRejectRef.current;
    clearPendingChallenge();
    reject(error);
  };

  const renderTurnstile = () => {
    if (!sitekey) {
      return Promise.reject(turnstileError("Turnstile site key missing", { phase: "config" }));
    }

    if (widgetIdRef.current !== null) {
      return Promise.resolve(widgetIdRef.current);
    }

    if (renderPromiseRef.current) {
      return renderPromiseRef.current;
    }

    renderPromiseRef.current = loadTurnstileScript()
      .then((turnstile) => {
        if (!mountedRef.current || !containerRef.current) {
          throw turnstileError("Turnstile container unavailable", { phase: "render" });
        }

        try {
          const widgetId = turnstile.render(containerRef.current, {
            sitekey,
            execution: "execute",
            appearance: "execute",
            callback: resolveChallenge,
            "error-callback": (code) => rejectChallenge(turnstileError("Turnstile challenge failed", {
              phase: "challenge",
              code
            })),
            "expired-callback": () => rejectChallenge(turnstileError("Turnstile challenge expired", {
              phase: "challenge"
            }))
          });

          if (typeof widgetId === "undefined") {
            throw turnstileError("Turnstile failed to render", { phase: "render" });
          }

          widgetIdRef.current = widgetId;
          return widgetIdRef.current;
        } catch (error) {
          error.turnstileDetails = error.turnstileDetails || {
            phase: "render",
            original_name: error.name,
            original_message: error.message
          };
          throw error;
        }
      })
      .catch((error) => {
        renderPromiseRef.current = null;
        throw error;
      });

    return renderPromiseRef.current;
  };

  useEffect(() => {
    mountedRef.current = true;
    renderTurnstile().catch(() => {});

    return () => {
      mountedRef.current = false;
      rejectChallenge(turnstileError("Turnstile challenge unmounted", { phase: "unmount" }));

      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
      }

      widgetIdRef.current = null;
      renderPromiseRef.current = null;
    };
  }, [sitekey]);

  useImperativeHandle(ref, () => ({
    executeAsync: () => {
      if (pendingRejectRef.current) {
        rejectChallenge(turnstileError("Turnstile challenge superseded", { phase: "execute" }));
      }

      return renderTurnstile().then((widgetId) => new Promise((resolve, reject) => {
        pendingResolveRef.current = resolve;
        pendingRejectRef.current = reject;
        timeoutRef.current = window.setTimeout(() => {
          rejectChallenge(turnstileError("Turnstile challenge timed out", { phase: "challenge" }));
        }, TURNSTILE_TIMEOUT);

        try {
          window.turnstile.reset(widgetId);
          window.turnstile.execute(`#${containerIdRef.current}`);
        } catch (error) {
          error.turnstileDetails = {
            phase: "execute",
            original_name: error.name,
            original_message: error.message
          };
          rejectChallenge(error);
        }
      }));
    },
    reset: () => {
      clearPendingChallenge();

      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
      }
    }
  }));

  return <div id={containerIdRef.current} ref={containerRef} />;
});

TurnstileChallenge.displayName = "TurnstileChallenge";

TurnstileChallenge.propTypes = {
  sitekey: PropTypes.string
};

export default TurnstileChallenge;
